import React, { useContext, useMemo } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import { Charge } from '@r6digital/rapidstor-types/react/entities/Charge';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { LineItem } from '@r6digital/rapidstor-types/types/Merchandise';
import { CostRetrieveResponse } from '@r6digital/rapidstor-types/types/Reservation';

import { transformCostRetrieve } from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import horizontalClasses from './PricingTableHorizontal.module.scss';

interface Props {
    costRetrieve: CostRetrieveResponse | undefined;
    charges: Charge[] | undefined;
    cart?: LineItem[];
    merchandiseDiscount: number;
}

export default function HorizontalPricingTable(props: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { currencySymbol } = location.rapidstor;
    const { rapidstorMainColor } = useContext(JourneyContext);

    const {
        costRetrieve, charges, cart, merchandiseDiscount,
    } = props;

    const totalCost = useMemo(() => {
        if (!costRetrieve && !charges && !cart) return 0;
        return transformCostRetrieve(
            charges,
            costRetrieve,
            cart,
            merchandiseDiscount,
        ).reduce((acc, item) => {
            if (item.total) {
                return acc + item.total;
            }
            return acc + item.chargeAmount + item.taxAmount - item.discountAmount;
        }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costRetrieve, charges, cart, merchandiseDiscount]);

    return (
        <table data-testid="pricing-table" className={horizontalClasses.pricingTable}>
            <thead>
                <tr>
                    <th>{t('wizard:pricingTable:charge')}</th>
                    <th className={horizontalClasses.desktop}>
                        {t('wizard:pricingTable:amount')}
                    </th>
                    <th className={horizontalClasses.desktop}>
                        {t('wizard:pricingTable:discount')}
                    </th>
                    <th className={horizontalClasses.desktop}>
                        {t('wizard:pricingTable:tax')}
                    </th>
                    <th>{t('wizard:pricingTable:total')}</th>
                </tr>
            </thead>
            {costRetrieve || charges ? (
                <tbody>
                    {transformCostRetrieve(
                        charges,
                        costRetrieve,
                        cart,
                        merchandiseDiscount,
                    )
                        .filter((item) => item.chargeAmount > 0 || item.discountAmount > 0)
                        .map((item, index) => (
                            <tr key={`${index}_crItem_idx`}>
                                <td>
                                    <span>{item.chargeDescription}</span>
                                    {item.chargePeriod && (
                                        <span className={horizontalClasses.chargePeriod}>
                                            {item.chargePeriod}
                                        </span>
                                    )}
                                    {item.discountAmount > 0 && item.discountName && (
                                        <span className={horizontalClasses.discountName}>
                                            {`(${item.discountName})`}
                                        </span>
                                    )}
                                </td>
                                <td className={horizontalClasses.desktop}>
                                    {currencySymbol}
                                    {item.chargeAmount.toFixed(2)}
                                </td>
                                <td className={horizontalClasses.desktop}>
                                    {currencySymbol}
                                    {item.discountAmount.toFixed(2)}
                                </td>
                                <td className={horizontalClasses.desktop}>
                                    {currencySymbol}
                                    {item.taxAmount.toFixed(2)}
                                </td>
                                <td>
                                    {currencySymbol}
                                    {(
                                        item.total
                    ?? item.chargeAmount + item.taxAmount - item.discountAmount
                                    ).toFixed(2)}
                                </td>
                            </tr>
                        ))}
                </tbody>
            ) : (
                <tbody>
                    <tr>
                        <td>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </td>
                        <td className={horizontalClasses.desktop}>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </td>
                        <td className={horizontalClasses.desktop}>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </td>
                        <td className={horizontalClasses.desktop}>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </td>
                        <td>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </td>
                    </tr>
                </tbody>
            )}
            <tfoot>
                <tr>
                    <td />
                    <td className={horizontalClasses.desktop} />
                    <td className={horizontalClasses.desktop} />
                    <td className={horizontalClasses.desktop} />
                    {costRetrieve || charges ? (
                        <td>
                            {t('wizard:pricingTable:total')}
                            :
                            {currencySymbol}
                            {totalCost.toFixed(2)}
                        </td>
                    ) : (
                        <td>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="30"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="30" />
                            </ContentLoader>
                        </td>
                    )}
                </tr>
            </tfoot>
        </table>
    );
}
